import { useNavigate } from "react-router-dom";
import { ImageOff, ArrowRight } from "lucide-react";
import AddToCartButton from "./cart/AddToCartButton";

/**
 * ProductGridCard — design_framework.md spec
 * Portrait card, 240px wide. Glass hover, gradient image wash, lift animation.
 * Mobile: full swipe card via .product-card-mobile class.
 *
 * Props:
 *   product    — product object
 *   onBuyNow   — optional override; defaults to /product/:id
 *   mobile     — bool: adds mobile swipe sizing classes
 */
export default function ProductGridCard({ product, onBuyNow, mobile = false }) {
  const navigate = useNavigate();
  const id = product?.mongo_id || product?.id;

  const displayImg = product?.photo_urls?.[0] || product?.img_url || null;

  const hasDiscount =
    product?.original_price &&
    product?.sale_price &&
    product.sale_price < product.original_price;

  const discountPct = hasDiscount
    ? Math.round(
        ((product.original_price - product.sale_price) / product.original_price) * 100
      )
    : null;

  const fmtPrice = (v) =>
    v?.toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 });

  const handleClick = () => navigate(`/product/${id}`);

  const handleBuyNow = (e) => {
    e.stopPropagation();
    if (onBuyNow) onBuyNow(product);
    else navigate(`/product/${id}`);
  };

  return (
    <div
      onClick={handleClick}
      className={`product-card flex flex-col ${mobile ? "product-card-mobile" : ""}`}
      style={{ width: mobile ? undefined : 270, flexShrink: 0 }}
    >
      {/* ── Image ── */}
      <div
        className="product-card__img flex items-center justify-center"
        style={{ height: 200, borderRadius: "16px 16px 0 0" }}
      >
        {displayImg ? (
          <img
            src={displayImg}
            alt={product.product_name || "Product"}
            style={{ width: "100%", height: "100%", objectFit: "cover" }}
          />
        ) : (
          <div className="flex flex-col items-center gap-2 text-muted-foreground/25 w-full h-full items-center justify-center" style={{ background: "rgba(255,255,255,0.03)" }}>
            <ImageOff className="h-8 w-8" />
            <span className="font-mono text-[9px] uppercase tracking-widest">Photo Coming Soon</span>
          </div>
        )}

        {/* Discount badge */}
        {discountPct && (
          <div
            className="chip chip-discount absolute top-3 left-3 z-10"
            style={{ borderRadius: "8px", padding: "3px 10px" }}
          >
            {discountPct}% OFF
          </div>
        )}
      </div>

      {/* ── Content ── */}
      <div className="flex flex-col flex-1 p-4 gap-3">
        {/* Category chip */}
        {product?.machine_type && (
          <span className="chip chip-primary self-start text-[10px]">
            {product.machine_type}
          </span>
        )}

        {/* Name */}
        <p className="font-heading font-semibold text-base text-foreground leading-tight line-clamp-2"
          style={{ fontFamily: "'Oswald', sans-serif" }}>
          {product?.product_name || "Unnamed Machine"}
        </p>

        {/* Tagline */}
        {product?.tagline && (
          <p className="font-body text-xs text-muted-foreground leading-snug line-clamp-2">
            {product.tagline}
          </p>
        )}

        {/* Price */}
        <div className="mt-auto pt-3 border-t border-border">
          {hasDiscount ? (
            <div className="flex flex-col gap-1">
              <div className="flex items-baseline gap-2">
                <span
                  className="font-heading font-bold text-lg"
                  style={{ color: "var(--vb-blue)", fontFamily: "'Oswald', sans-serif" }}
                >
                  ${fmtPrice(product.sale_price)}
                </span>
                <span className="font-body text-xs line-through" style={{ color: "rgba(255,255,255,0.5)" }}>
                  ${fmtPrice(product.original_price)}
                </span>
              </div>
              <span className="chip chip-savings self-start" style={{ borderRadius: 6 }}>
                Save ${fmtPrice(product.original_price - product.sale_price)}
              </span>
            </div>
          ) : product?.sale_price ? (
            <span
              className="font-heading font-bold text-lg"
              style={{ color: "var(--vb-blue)", fontFamily: "'Oswald', sans-serif" }}
            >
              ${fmtPrice(product.sale_price)}
            </span>
          ) : (
            <span className="font-body text-xs text-muted-foreground italic">
              Price on request
            </span>
          )}
        </div>

        {/* CTA row — clear hierarchy: primary action + secondary view */}
        <div className="flex gap-2">
          <button
            onClick={product?.sale_price ? handleBuyNow : (e) => { e.stopPropagation(); navigate(`/product/${id}`); }}
            className="btn-primary flex-1 justify-center"
            style={{ height: 44, padding: "0 14px", fontSize: 12, borderRadius: 12 }}
          >
            {product?.sale_price ? "Get This Machine" : "View Details"}
          </button>

          <button
            onClick={(e) => { e.stopPropagation(); navigate(`/product/${id}`); }}
            className="btn-ghost flex items-center gap-1"
            style={{
              height: 44,
              padding: "0 12px",
              fontSize: 12,
              borderRadius: 12,
            }}
          >
            Details <ArrowRight className="h-3 w-3" />
          </button>
        </div>

        {/* Trust micro-copy */}
        {product?.sale_price && (
          <p className="font-body text-[10px] text-center" style={{ color: "rgba(255,255,255,0.35)", marginTop: -4 }}>
            ✔ Verified · 1-Yr Warranty · ~21 day shipping
          </p>
        )}
      </div>
    </div>
  );
}
